import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Monitor, GitBranch, Cloud, Database, ArrowRight, Bot, Container, CheckCircle2 } from "lucide-react";

const services = [
  {
    id: "cloudops",
    icon: Monitor,
    tag: "Operate",
    title: "CloudOps as a Service",
    lede: "24/7 managed cloud operations. We run your infrastructure so your team can run your product.",
    features: [
      "Round-the-clock infrastructure monitoring",
      "Incident response with defined SLAs",
      "FinOps and cost right-sizing",
      "Performance tuning and capacity planning",
      "Automated patching and vulnerability response",
      "SOC 2 / HIPAA / ISO 27001 compliance",
    ],
    outcomes: ["-99.5% downtime", "-40% cloud spend", "24/7 named on-call", "Enterprise SLAs"],
  },
  {
    id: "devops",
    icon: GitBranch,
    tag: "Automate",
    title: "DevOps as a Service",
    lede: "Ship faster and safer. CI/CD, IaC, and GitOps engineered for velocity without regressions.",
    features: [
      "CI/CD pipelines (GitHub Actions, GitLab, Argo)",
      "Infrastructure as Code (Terraform, Pulumi, CDK)",
      "Container orchestration and Helm",
      "GitOps workflows (ArgoCD, Flux)",
      "Automated testing and quality gates",
      "Blue-green and canary release strategies",
    ],
    outcomes: ["10× deploy frequency", "-95% manual errors", "Faster time to market", "Consistent envs"],
  },
  {
    id: "kubernetes",
    icon: Container,
    tag: "Modernize",
    title: "Kubernetes & Container Modernization",
    lede: "Move legacy workloads to EKS, AKS, GKE, or self-managed clusters — with production observability from day one.",
    features: [
      "Managed cluster setup (EKS / AKS / GKE)",
      "Containerization and refactor to microservices",
      "Helm + GitOps (ArgoCD, Flux)",
      "Service mesh (Istio, Linkerd)",
      "Autoscaling (HPA, VPA, Karpenter, KEDA)",
      "Observability (Prometheus, Grafana, Loki, OTel)",
    ],
    outcomes: ["+70% utilization", "Dev-prod parity", "Safer rollouts", "Vendor-neutral"],
  },
  {
    id: "migration",
    icon: Cloud,
    tag: "Move",
    title: "Cloud Migration",
    lede: "Lift, shift, and modernize workloads with zero downtime. From on-prem to cloud or between clouds.",
    features: [
      "Cloud readiness and TCO assessment",
      "Migration wave planning",
      "Data migration and validation (DMS, DataSync)",
      "Application refactoring where it pays off",
      "Network, identity, and security setup",
      "Post-migration cost and performance optimization",
    ],
    outcomes: ["Zero downtime", "Lower TCO", "Modern architecture", "Documented handoff"],
  },
  {
    id: "ai-agents",
    icon: Bot,
    tag: "Deploy",
    title: "Custom AI Agents",
    lede: "Production LLM agents deployed to your VPC or on-prem. Own your data, your models, your workflows.",
    features: [
      "LLM selection and fine-tuning",
      "RAG pipelines and vector databases",
      "Agent orchestration (LangGraph, CrewAI, MCP)",
      "Deploy to your VPC, on-prem, or managed cloud",
      "Guardrails, evals, and observability",
      "Tool integrations (databases, SaaS APIs, internal services)",
    ],
    outcomes: ["Own your data & models", "No per-token lock-in", "Enterprise security", "Auditable workflows"],
  },
  {
    id: "implementation",
    icon: Database,
    tag: "Build",
    title: "Cloud Project Implementation",
    lede: "From greenfield architecture to multi-cloud data platforms. Production-ready, documented, and yours.",
    features: [
      "Reference architecture design",
      "Multi-cloud and hybrid solutions",
      "Serverless and microservices",
      "Data platforms (Snowflake, BigQuery, Databricks)",
      "API gateway and service mesh",
      "Disaster recovery and multi-region",
    ],
    outcomes: ["Production from day one", "Scalable by design", "Best practices baked in", "Full documentation"],
  },
];

const Services = () => {
  return (
    <Layout>
      <Helmet>
        <title>Services — Cloudease</title>
        <meta name="description" content="CloudOps, DevOps, Kubernetes, Migration, Custom AI Agents, and Cloud Project Implementation on AWS, Azure, and GCP." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-aurora animate-aurora" aria-hidden />
        <div className="absolute inset-0 bg-grid opacity-30 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_80%)]" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-5">Services</p>
            <h1 className="text-5xl md:text-7xl font-heading font-semibold tracking-tight mb-6">
              Everything you need <br /> to run <span className="text-gradient">production cloud.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl">
              Six deep specialties. One accountable team. All delivered in your accounts, with clear handoff.
            </p>
          </motion.div>

          {/* index chips */}
          <div className="mt-12 flex flex-wrap gap-2">
            {services.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="px-4 py-2 rounded-full glass text-xs font-mono hover:border-primary/40 transition-colors"
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Service sections */}
      {services.map((s, idx) => (
        <section
          key={s.id}
          id={s.id}
          className={`py-24 border-b border-border/60 ${idx % 2 === 1 ? "bg-secondary/20" : ""}`}
        >
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="lg:col-span-5"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-12 w-12 rounded-xl bg-brand/10 border border-primary/20 flex items-center justify-center">
                    <s.icon className="h-6 w-6 text-primary" />
                  </div>
                  <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{s.tag}</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight mb-5">{s.title}</h2>
                <p className="text-muted-foreground leading-relaxed mb-8">{s.lede}</p>

                <div className="grid grid-cols-2 gap-3 mb-8">
                  {s.outcomes.map((o) => (
                    <div key={o} className="px-4 py-3 rounded-lg glass text-sm font-medium">{o}</div>
                  ))}
                </div>

                <Link to="/contact">
                  <Button variant="gradient" size="lg" className="rounded-full">
                    Discuss your project <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="lg:col-span-7"
              >
                <div className="p-8 rounded-2xl glass">
                  <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-6">What's included</h3>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {s.features.map((f) => (
                      <div key={f} className="flex items-start gap-2.5 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <span className="text-foreground/90">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh opacity-40 animate-aurora" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight mb-5">
            Not sure which service fits?
          </h2>
          <p className="text-muted-foreground mb-8">
            Book a 30-minute call. We'll listen, ask questions, and recommend a path — no sales script.
          </p>
          <Link to="/contact">
            <Button variant="gradient" size="xl" className="rounded-full">
              Talk to an engineer <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
